import React, { useCallback, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import api from '../../../api';
import {
  FieldCheckbox,
  FieldTextInput,
  Form,
  Modal,
  PrimaryButton,
  SecondaryButton,
} from '../../../components';

import css from './CustomerContactedModal.module.css';

const CustomerContactedModal = props => {
  const { className, isOpen, onClose, listing } = props;

  const intl = useIntl();
  const dispatch = useDispatch();

  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { metadata } = listing.attributes || {};
  const { hasContactedLeasingUser } = metadata || {};

  const classes = classNames(css.root, className);

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  const onSubmit = async values => {
    try {
      setSubmitInProgress(true);
      setSubmitError(null);

      await api.listings.updateListing(listing.id.uuid, {
        metadata: {
          hasContactedLeasingUser: Boolean(values.hasContactedLeasingUser),
          leaseExpirationOption: 'not-set',
          expirationOptionComment: values.expirationOptionComment,
          dateLeasingUserContacted: new Date(),
        },
      });

      onClose();
      window.location.reload();
    } catch (error) {
      setSubmitError(error);
      console.error(error);
    } finally {
      setSubmitInProgress(false);
    }
  };

  return (
    <Modal
      id="CustomerContactedModal"
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
      className={classes}
    >
      <FinalForm
        onSubmit={onSubmit}
        initialValues={{ hasContactedLeasingUser: false, expirationOptionComment: '' }}
        render={formProps => {
          const { handleSubmit, values } = formProps;

          const disableSubmit =
            submitInProgress ||
            !values?.hasContactedLeasingUser ||
            hasContactedLeasingUser ||
            values?.hasContactedLeasingUser?.length === 0;

          return (
            <Form onSubmit={handleSubmit} className={css.form}>
              <div className={css.title}>
                {intl.formatMessage({
                  id: 'ContractLeaseActions.customerContacted',
                })}
              </div>

              <FieldCheckbox
                name="hasContactedLeasingUser"
                id="hasContactedLeasingUser"
                value={String(values?.hasContactedLeasingUser || hasContactedLeasingUser)}
                label={intl.formatMessage({
                  id: 'ContractLeaseActions.hasContactedLeasingUser',
                })}
              />

              <FieldTextInput
                name="expirationOptionComment"
                id="expirationOptionComment"
                type="text"
                className={css.commentField}
                disabled={disableSubmit}
                label={intl.formatMessage({
                  id: 'ContractLeaseActions.expirationOptionComment',
                })}
              />

              {submitError && (
                <p className={css.error}>{intl.formatMessage({ id: 'General.error' })}</p>
              )}

              <div className={css.btns}>
                <SecondaryButton type="button" onClick={onClose}>
                  {intl.formatMessage({ id: 'General.cancel' })}
                </SecondaryButton>

                <PrimaryButton
                  type="submit"
                  disabled={disableSubmit}
                  inProgress={submitInProgress}
                  className={css.submit}
                >
                  {intl.formatMessage({ id: 'General.confirm' })}
                </PrimaryButton>
              </div>
            </Form>
          );
        }}
      />
    </Modal>
  );
};

export default CustomerContactedModal;
